import isAlpha from 'validator/lib/isAlpha';
import isMobilePhone from 'validator/lib/isMobilePhone';

import { stateInitialWeCanShipToDictionary } from '../../../server/graphql/helper/statesWeCanShipTo';
import { removeNonNumericCharacters } from '../../utils/formattingHelpers';

export const statesAbbrev = [
  'AL',
  'AK',
  'AS',
  'AZ',
  'AR',
  'CA',
  'CO',
  'CT',
  'DE',
  'DC',
  'FM',
  'FL',
  'GA',
  'GU',
  'HI',
  'ID',
  'IL',
  'IN',
  'IA',
  'KS',
  'KY',
  'LA',
  'ME',
  'MH',
  'MD',
  'MA',
  'MI',
  'MN',
  'MS',
  'MO',
  'MT',
  'NE',
  'NV',
  'NH',
  'NJ',
  'NM',
  'NY',
  'NC',
  'ND',
  'MP',
  'OH',
  'OK',
  'OR',
  'PW',
  'PA',
  'PR',
  'RI',
  'SC',
  'SD',
  'TN',
  'TX',
  'UT',
  'VT',
  'VI',
  'VA',
  'WA',
  'WV',
  'WI',
  'WY',
];

export function isStateAbbrev(abbrev) {
  return statesAbbrev.indexOf(abbrev) !== -1;
}

export function removeSpaces(text: string): string {
  return text.replace(/\s/g, '');
}

function isNotAlpha(text: string): boolean {
  return !isAlpha(removeSpaces(text));
}

export function checkContainsPOBox(line: string): boolean {
  const pattern = /^(?!.*(?:(.*((p|post)[-.\s]*(o|off|office)[-.\s]*(box|bin)[-.\s]*)|.*((p |post)[-.\s]*(box|bin)[-.\s]*)))).*$/i;
  if (!line.match(pattern)) return true;
  return false;
}

export function isValidUSZip(usZip: string): boolean {
  return /^\d{5}(-\d{4})?$/.test(usZip);
}

export function checkContainsNumber(addressLineOne: string) {
  const containsDigits = /[0123456789]/.test(addressLineOne);
  const containsNumbers = /one/.test(addressLineOne.toLowerCase());
  return containsNumbers || containsDigits;
}

export function checkContainsLetter(addressLineOne: string) {
  return /[abcdefghijklmnopqrstuvwxyz]/.test(addressLineOne.toLowerCase());
}

export function checkCountry(country: string) {
  if (!country) {
    return true;
  }
  const lowerCountry = country.toLowerCase();
  switch (true) {
    case lowerCountry === 'usa':
    case lowerCountry === 'us':
    case lowerCountry === 'united states':
    case lowerCountry === '':
    case lowerCountry === ' ':
      return true;
    default:
      return false;
  }
}

// TODO(gemma): Standardize these to returning ENUMs and then we map them to display text; see errorMessages.js
// Could be good for supporting multiple languages too.
export function getAddressFormError(
  field: string,
  text: string,
  isShipping: boolean,
): string | void {
  const fieldDictionary = {
    addressLineOne: () => {
      const containsNumber = checkContainsNumber(text);
      const containsPOBox = checkContainsPOBox(text);
      const containsLetter = checkContainsLetter(text);
      switch (true) {
        case containsPOBox && isShipping:
          return 'We cannot ship to PO Boxes';
        case !containsNumber:
          return 'Please check that address contains a street number';
        case !containsLetter:
          return 'Please include the name of your street along with the building number';
        default:
          return undefined;
      }
    },
    addressLineTwo: () => {
      return checkContainsPOBox(text)
        ? isShipping
          ? 'We cannot ship to PO Boxes'
          : undefined
        : undefined;
    },
    zip: () => {
      return isValidUSZip(text) ? undefined : 'Please enter a valid zip code';
    },
    phoneNumber: () => {
      return isMobilePhone(removeNonNumericCharacters(text), 'en-US')
        ? undefined
        : 'Please enter a valid phone number';
    },
    firstName: () => undefined,
    lastName: () => undefined,
    city: () => {
      return isNotAlpha(removeSpaces(text))
        ? 'Please enter a valid city'
        : undefined;
    },
    state: () => {
      if (text.length < 2) {
        return 'Please enter a valid state abbreviation';
      }
      const stateText = text.toUpperCase();

      if (isShipping) {
        if (!stateInitialWeCanShipToDictionary[stateText]) {
          return 'We cannot ship to this state';
        }
        if (!isStateAbbrev(stateText)) {
          return 'Please enter a valid state abbreviation';
        }

        return undefined;
      }

      return !isStateAbbrev(stateText)
        ? 'Please enter a valid state abbreviation'
        : undefined;
    },
    country: () => {
      if (!checkCountry(text)) {
        return 'We can only ship to the US';
      }
    },
    expirationMonth: () => {
      if (text.length < 2) {
        return 'Please enter a valid expiration month';
      }
    },
    expirationYear: () => {
      if (text.length < 2) {
        return 'Please enter a valid expiration year';
      }
    },
    cvc: () => {
      if (text.length < 3) {
        return 'Please enter your CVC/CVV';
      }
    },
  };
  return fieldDictionary[field] ? fieldDictionary[field](text) : undefined;
}
